import React, { useState, useEffect, ReactElement, ChangeEvent } from 'react';

import { Member } from '../../../../../types/entity.types';

import Modal from '../../../../common/modal/Modal';
import TextInput from '../../../../common/input/text-input/TextInput';
import Checkbox from '../../../../common/input/checkbox/Checkbox';
import Button from '../../../../common/button/Button';

import './member-modal.scss';

type Props = {
  member?: Member;
  close: () => void;
  createMember: (memberToSave: Member, close: () => void) => void;
  updateMember: (memberToSave: Member, close: () => void) => void;
  deleteMember: (externalReference: string, close: () => void) => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
};

const MemberModal = ({
  member,
  close,
  createMember,
  updateMember,
  deleteMember,
  sendMemberActivationCommunications
}: Props): ReactElement => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [paid, setPaid] = useState<boolean>(false);
  const [staff, setStaff] = useState<boolean>(false);
  const [shouldSendCommunications, setShouldSendCommunications] = useState<boolean>(false);

  useEffect((): void => {
    if (!member) return;

    setFirstName(member.firstName ?? '');
    setLastName(member.lastName ?? '');
    setEmail(member.emailAddress ?? '');
    setPhone(member.phoneNumber ?? '');
    setStaff(!!member.isStaff);
    setPaid(!!member.paid);
    setShouldSendCommunications(false);
  }, [member]);

  if (!member) return <React.Fragment />;

  const hasMemberChanged = (newMember: Member): boolean => {
    if (member.firstName !== newMember.firstName) return true;
    if (member.lastName !== newMember.lastName) return true;
    if (member.emailAddress !== newMember.emailAddress) return true;
    if (member.phoneNumber !== newMember.phoneNumber) return true;
    if (!!member.isStaff !== newMember.isStaff) return true;

    return !!member.paid !== newMember.paid;
  };

  const saveMember = (): void => {
    const memberToSave: Member = {
      externalReference: member.externalReference,
      firstName: firstName,
      lastName: lastName,
      emailAddress: email,
      phoneNumber: phone,
      isStaff: staff,
      paid
    };

    if (memberToSave.externalReference) {
      if (!hasMemberChanged(memberToSave)) {
        close();
        return;
      }

      updateMember(memberToSave, close);
    } else {
      createMember(memberToSave, close);
    }
  };

  const handleSaveClick = (): void => {
    if (shouldSendCommunications) {
      sendMemberActivationCommunications(member.externalReference ?? '', !!phone, !!email);
    }

    saveMember();
  };

  const handleDeleteClick = (): void => {
    deleteMember(member.externalReference ?? '', close);
  };

  return (
    <Modal show={!!member}>
      <section className="member-modal" data-testid="member-modal">
        <div className="member-modal__header">{`${member.externalReference ? 'Edit' : 'Add'} Member`}</div>
        <div className="member-modal__inputs">
          <TextInput
            label="First Name:"
            name="first-name"
            value={firstName}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setFirstName(target.value)}
            autoFocus
          />
          <TextInput
            label="Last Name:"
            name="last-name"
            value={lastName}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setLastName(target.value)}
          />
          <TextInput
            label="Email:"
            name="email"
            value={email}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setEmail(target.value)}
          />
          <TextInput
            label="Phone:"
            name="phone-number"
            value={phone}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPhone(target.value)}
          />
          <Checkbox label="Staff Member:" name="staff" value={staff} onChange={setStaff} />
          <Checkbox label="Paid:" name="paid" value={paid} onChange={setPaid} />
          {member?.externalReference && (
            <Checkbox
              label="Send Activation Link:"
              name="send-communications"
              value={shouldSendCommunications}
              onChange={setShouldSendCommunications}
            />
          )}
        </div>
        <div className="member-modal__button-container">
          <Button text="Cancel" onClick={close} />
          {member.externalReference && <Button text="Delete" onClick={handleDeleteClick} />}
          <Button text="Save" onClick={handleSaveClick} disabled={!firstName || !lastName || !email} />
        </div>
      </section>
    </Modal>
  );
};

export default MemberModal;
